import { Container, FilterChips } from '@components/Filter';
import { FilterButton } from '@components/Filter/FilterButton';
import { FilterDrawer } from '@components/Filter/FilterDrawer';
import { hasValue } from '@components/Filter/filter.utils';
import { FlexRow } from '@components/FlexRow';
import StarIcon from '@components/Icon/Star';
import StarActiveIcon from '@components/Icon/StarActiveIcon';
import InputCheckboxGroup from '@components/Input/InputCheckboxGroup';
import InputSliderRangeStatic from '@components/Input/InputSliderRange/static';
import InputAutocomplete from '@components/InputAutocomplete';
import { SearchBarWithNumber } from '@components/SearchbarWithNumber';
import { useAuth } from '@contexts/AuthContext';
import JobApplicationFilterRecruitmentMethod from '@modules/jobApplications/components/JobApplicationFilterRecruitmentMethod';
import JobApplicationFilterStatus from '@modules/jobApplications/components/JobApplicationFilterStatus';
import JobApplicationPremiumFilterStatus from '@modules/jobApplications/components/JobApplicationPremiumFilterStatus';
import { useJobApplicationFilters } from '@modules/jobApplications/hooks/useJobApplicationFilters';
import { jobApplicationStatusLabels, premiumJobApplicationStatusLabels } from '@modules/jobApplications/statuses';
import RefreshIcon from '@mui/icons-material/Refresh';
import {
    Box,
    Button,
    CircularProgress,
    FormGroup,
    FormLabel,
    IconButton,
    Rating,
    Tooltip
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { ReactNode, useMemo, useState } from 'react';

import { FormProvider, useForm } from 'react-hook-form';
import { AvailableJobApplicationFilters } from './hooks/useJobApplicationFilters';

import { renderValue } from '../../components/Filter/filter.utils';

export const ButtonCustom = styled(Button)(({ theme }) => ({
    background: 'none',
    border: `1px solid ${theme.palette.secondary.main}`,
    fontWeight: 700,
    fontSize: '16px',
    lineHeight: '24px',
    padding: '12px 24px',
    maxHeight: '48px',
}));

/**
 * Build a custom label renderer for job application filter chips.
 * Created inside the component to access available filters for ID→label resolution.
 */
const createRenderJobApplicationChipLabel = (
    availableFilters: AvailableJobApplicationFilters,
): ((props: { key: string; value: any; label: string }) => ReactNode | undefined) => {
    // Build lookup map: ID → label from available upercut assignee options
    const upercutAssigneeLabelMap = new Map((availableFilters.upercutAssigneeIds ?? []).map(o => [o.value, o.label]));

    return ({ key, value, label }: { key: string; value: any; label: string }): ReactNode | undefined => {
        // Handle job application status with translated labels
        if (key === 'jobApplicationStatus') {
            const translatedValue =
                jobApplicationStatusLabels[value as keyof typeof jobApplicationStatusLabels] ?? value;
            return renderValue({ label, key, value: translatedValue });
        }

        // Handle premium status with translated labels
        if (key === 'premiumStatus') {
            const translatedValue =
                premiumJobApplicationStatusLabels[value as keyof typeof premiumJobApplicationStatusLabels] ?? value;
            return renderValue({ label, key, value: translatedValue });
        }

        // Handle recruitment method with chip-specific labels
        if (key === 'recruitmentMethod') {
            const recruitmentMethodLabels: Record<string, string> = {
                premium: 'Premium',
                direct_email: 'Classique',
            };
            const translatedValue = recruitmentMethodLabels[value] ?? value;
            return renderValue({ label, key, value: translatedValue });
        }

        // Handle upercut assignee: resolve ID to name via the lookup map
        if (key === 'upercutAssigneeIds') {
            const resolvedLabel = upercutAssigneeLabelMap.get(value) ?? value;
            return renderValue({ label, key, value: resolvedLabel });
        }

        // Return undefined to use default rendering
        return undefined;
    };
};

type CityFilterOption = {
    label: string;
    value: string;
    address?: {
        postalCode?: string | number;
    };
};

export type IJobApplicationDetailFilter = {
    talentId?: string[];
    proId?: string[];
    jobNumber?: string[];
    jobApplicationStatus?: string[];
    salary?: {
        min: number | null;
        max: number | null;
    };
    locations?: CityFilterOption[];
    jobOfferLocations?: CityFilterOption[];
    workingHours?: {
        min: number | null;
        max: number | null;
    };
    sectorIds?: string[];
    yearsOfExperience?: {
        min: number | null;
        max: number | null;
    };
    language?: string;
    createdBy?: string;
    brandIds?: string[];
    createdAt?: {
        start: string;
        end: string;
    };
    publishedDate?: {
        start: string;
        end: string;
    };
    jobApplicationType?: string;
    upercutAssigneeIds?: string[];
    recruitmentMethod?: string;
    premiumStatus?: string[];
    premiumSubStatus?: string[];
    resumeScore?: {
        min: number | null;
        max: number | null;
    };
    upercutAverageRating?: number | null;
};

export const defaultJobApplicationFilters: IJobApplicationDetailFilter = {
    talentId: [],
    proId: [],
    salary: {
        min: null,
        max: null,
    },
    locations: [],
    jobOfferLocations: [],
    workingHours: {
        min: null,
        max: null,
    },
    jobApplicationStatus: [],
    jobApplicationType: '',
    recruitmentMethod: '',
    premiumStatus: [],
    premiumSubStatus: [],
    sectorIds: [],
    yearsOfExperience: {
        min: null,
        max: null,
    },
    language: '',
    createdBy: '',
    brandIds: [],
    createdAt: {
        start: null,
        end: null,
    },
    publishedDate: {
        start: null,
        end: null,
    },
    upercutAssigneeIds: [],
    resumeScore: {
        min: null,
        max: null,
    },
    upercutAverageRating: null,
};

export const filterLabels = {
    proId: 'Établissement',
    jobApplicationStatus: 'Statut de candidature',
    salary: 'Salary',
    locations: `CP, ville, département, région`,
    jobOfferLocations: "Ville de l'offre",
    workingHours: 'Working hours/week',
    sectorIds: 'Secteur d’activité',
    experience: 'Expérience',
    language: 'Language',
    createdBy: 'Created by',
    brandIds: 'Marque',
    upercutAssigneeIds: 'Équipe Upercut',
    createdAt: 'Created Date',
    publishedDate: 'Published Date',
    function: 'Fonction',
    rating: 'Score',
    upercutAverageRating: 'Score Upercut',
    contractTypes: 'Type de contrat',
    distance: `Distance`,
    diplomas: 'Diplômes',
    travail: 'Lieu de travail',
    recruitmentMethod: 'Type de recrutement',
    premiumStatus: 'Statut premium',
    resumeScore: 'Score du CV',
};

export const JobApplicationDetailFilter = ({
    viewMode,
    totalDocs,
    changeViewMode,
    onQueryChange,
    onSortChange,
    onFilterChange,
    filter,
    hideChipKeys = [],
    onRefresh,
    initialQuery,
}: {
    viewMode: string;
    totalDocs: number;
    changeViewMode: (event: React.MouseEvent<HTMLButtonElement>) => void;
    onQueryChange: (query: string) => void;
    onSortChange: (sort: { field: string; order: number }) => void;
    onFilterChange: (filter: IJobApplicationDetailFilter) => void;
    filter?: IJobApplicationDetailFilter;
    hideChipKeys?: string[];
    onRefresh?: (proId?: string) => void | Promise<void>;
    initialQuery?: string;
}) => {
    const { isUpercut } = useAuth();
    const [isDrawerOpen, setIsDrawerOpen] = useState(false);
    const [searchQuery, setSearchQuery] = useState(initialQuery ?? '');
    const [isRefreshing, setIsRefreshing] = useState(false);

    const methods = useForm({
        defaultValues: defaultJobApplicationFilters,
    });

    const upercutAverageRating = methods.watch('upercutAverageRating');
    const { setValue } = methods;

    const { availableFilters, filtersLoading } = useJobApplicationFilters(
        filter ?? defaultJobApplicationFilters,
        searchQuery,
    );

    const renderJobApplicationChipLabel = useMemo(
        () => createRenderJobApplicationChipLabel(availableFilters),
        [availableFilters],
    );

    /** Append " (N)" count to each option label */
    const withCount = useMemo(
        () => (opts: { value: string; label: string; count: number; groupLabel?: string }[]) =>
            opts.map(o => ({ value: o.value, label: `${o.label} (${o.count})`, groupLabel: o.groupLabel })),
        [],
    );

    const toggleDrawer = open => () => {
        setIsDrawerOpen(open);
    };

    const onApplyFilter = data => {
        onFilterChange(data);
        setIsDrawerOpen(false);
    };

    const onResetFilter = () => {
        onFilterChange(defaultJobApplicationFilters);
        methods.reset(defaultJobApplicationFilters);
        setIsDrawerOpen(false);
    };

    const handleQueryChange = (q: string) => {
        setSearchQuery(q);
        onQueryChange(q);
    };

    const hasVisibleActiveFilters = Object.entries(filter || {}).some(
        ([key, value]) => !hideChipKeys.includes(key) && hasValue(value),
    );

    return (
        <FormProvider {...methods}>
            <Container sx={{ marginBottom: '0px', overflowX: 'auto', overflowY: 'hidden' }}>
                <FlexRow>
                    <SearchBarWithNumber
                        onChange={handleQueryChange}
                        placeholder="Recherche..."
                        totalCount={totalDocs}
                        initialValue={initialQuery}
                    />
                    <FilterButton toggleDrawer={toggleDrawer} />
                    <JobApplicationFilterRecruitmentMethod
                        value={filter?.recruitmentMethod ?? ''}
                        onChange={recruitmentMethod =>
                            onFilterChange({
                                ...(filter ?? defaultJobApplicationFilters),
                                recruitmentMethod,
                            })
                        }
                    />
                </FlexRow>
                <FlexRow>
                    {/*<ButtonViewContainer>
                        <ButtonView onClick={changeViewMode} value="list" activeView={viewMode === 'list'}>
                            <i className="fi fi-br-list" />
                        </ButtonView>
                        <ButtonView disabled onClick={changeViewMode} value="grid" activeView={viewMode === 'grid'}>
                            <i className="fi fi-br-grid" />
                        </ButtonView>
                        <ButtonView disabled onClick={changeViewMode} value="kanban" activeView={viewMode === 'kanban'}>
                            <i className="fi fi-br-chart-kanban" />
                        </ButtonView>
                    </ButtonViewContainer>*/}
                    <JobApplicationFilterStatus
                        value={filter?.jobApplicationStatus ?? []}
                        onChange={statuses =>
                            onFilterChange({
                                ...(filter ?? defaultJobApplicationFilters),
                                jobApplicationStatus: statuses,
                            })
                        }
                    />
                    <JobApplicationPremiumFilterStatus
                        value={filter?.premiumStatus ?? []}
                        onChange={statuses =>
                            onFilterChange({
                                ...(filter ?? defaultJobApplicationFilters),
                                premiumStatus: statuses,
                            })
                        }
                    />
                    {onRefresh && (
                        <Tooltip title="Rafraîchir les données">
                            <span>
                                <IconButton
                                    onClick={async () => {
                                        setIsRefreshing(true);
                                        try {
                                            await onRefresh();
                                        } finally {
                                            setIsRefreshing(false);
                                        }
                                    }}
                                    disabled={isRefreshing}
                                    size="small"
                                    sx={{ color: theme => theme.palette.primary.main }}>
                                    {isRefreshing ? <CircularProgress size={20} color="inherit" /> : <RefreshIcon />}
                                </IconButton>
                            </span>
                        </Tooltip>
                    )}
                </FlexRow>
            </Container>
            {hasVisibleActiveFilters && (
                <Container>
                    <FlexRow sx={{ width: '100%', minWidth: 0, gap: '10px' }}>
                        <FilterChips
                            filter={filter}
                            labels={filterLabels}
                            defaultFilters={defaultJobApplicationFilters}
                            onFilterChange={onFilterChange}
                            hideKeys={hideChipKeys}
                            renderLabel={renderJobApplicationChipLabel}
                        />
                    </FlexRow>
                </Container>
            )}
            <FilterDrawer
                isDrawerOpen={isDrawerOpen}
                toggleDrawer={toggleDrawer}
                onApplyFilter={onApplyFilter}
                onResetFilter={onResetFilter}>
                {isUpercut && (
                    <FormGroup>
                        <FormLabel>{filterLabels.upercutAverageRating}</FormLabel>
                        <Box
                            sx={{
                                padding: '8px 16px',
                                borderRadius: '16px',
                                background: theme => theme.palette.disableFormBlack.main,
                                width: 'fit-content',
                            }}>
                            <Rating
                                value={upercutAverageRating ?? 0}
                                sx={{ color: '#ffd700', display: 'flex', alignItems: 'center', gap: '14px' }}
                                icon={<StarActiveIcon style={{ width: '24px', height: '24px' }} />}
                                emptyIcon={<StarIcon style={{ width: '24px', height: '24px' }} />}
                                onChange={(event, newValue) => {
                                    setValue('upercutAverageRating', newValue);
                                }}
                            />
                        </Box>
                    </FormGroup>
                )}
                {/* <FormGroup>
                    <FormLabel>{filterLabels.distance}</FormLabel>
                    <InputDistance
                        name="distance"
                        min={0}
                        max={150}
                        defaultValue={0}
                        units="km"
                        minLabel="0 km"
                        maxLabel="150 km"
                    />
                </FormGroup> */}
                <FormGroup>
                    <FormLabel>{filterLabels.locations}</FormLabel>
                    <InputAutocomplete
                        id="locations"
                        name="locations"
                        multiple
                        getOptionLabel={option => `${option.label} (${option.count})`}
                        options={availableFilters.cities}
                    />
                </FormGroup>
                <FormGroup>
                    <FormLabel>{filterLabels.jobOfferLocations}</FormLabel>
                    <InputAutocomplete
                        id="locations"
                        name="jobOfferLocations"
                        multiple
                        getOptionLabel={option => `${option.label} (${option.count})`}
                        options={availableFilters.jobOfferCities}
                    />
                </FormGroup>
                <FormGroup>
                    <FormLabel>{filterLabels.proId}</FormLabel>
                    <InputAutocomplete
                        id="proId"
                        name="proId"
                        multiple
                        loading={filtersLoading}
                        options={withCount(availableFilters.proIds)}
                    />
                </FormGroup>
                <FormGroup>
                    <FormLabel>{filterLabels.contractTypes}</FormLabel>
                    <InputAutocomplete
                        id="contractTypes"
                        name="contractTypes"
                        multiple
                        loading={filtersLoading}
                        options={withCount(availableFilters.contractTypes)}
                    />
                </FormGroup>
                <FormGroup>
                    <FormLabel>{filterLabels.function}</FormLabel>
                    <InputAutocomplete
                        id="professions"
                        name="position.professions"
                        multiple
                        loading={filtersLoading}
                        groupBy={option => option.groupLabel}
                        getOptionLabel={option =>
                            option.groupLabel ? `${option.groupLabel} - ${option.label}` : option.label
                        }
                        options={withCount(availableFilters.professions)}
                    />
                </FormGroup>
                <FormGroup>
                    <FormLabel>{filterLabels.sectorIds}</FormLabel>
                    <InputAutocomplete
                        id="sectorIds"
                        name="sectorIds"
                        multiple
                        loading={filtersLoading}
                        options={withCount(availableFilters.sectorIds)}
                    />
                </FormGroup>
                <FormGroup>
                    <FormLabel>{filterLabels.diplomas}</FormLabel>
                    <InputAutocomplete
                        id="diplomas"
                        name="diplomas"
                        multiple
                        loading={filtersLoading}
                        options={withCount(availableFilters.diplomaTypes)}
                    />
                </FormGroup>
                <FormGroup>
                    <FormLabel>{filterLabels.experience}</FormLabel>
                    <InputSliderRangeStatic
                        name="experience"
                        min={availableFilters.yearsOfExperience?.min ?? 0}
                        max={availableFilters.yearsOfExperience?.max ?? 25}
                        defaultMin={3}
                        defaultMax={10}
                        units="y"
                        minLabel="0 year"
                        maxLabel="25 year"
                        minDistance={0}
                    />
                </FormGroup>
                <FormGroup>
                    <InputCheckboxGroup
                        name="experienceTypes"
                        direction="row"
                        options={[
                            { label: 'Toute année', value: 'allYears' },
                            { label: 'Jeune diplômé', value: 'freshGraduate' },
                        ]}
                    />
                </FormGroup>
                <FormGroup>
                    <FormLabel>{filterLabels.salary}</FormLabel>
                    <InputSliderRangeStatic
                        name="salary"
                        min={availableFilters.salaryRange.min ?? 0}
                        max={availableFilters.salaryRange.max ?? 5000}
                        defaultMin={1000}
                        defaultMax={2000}
                        units="€"
                        minLabel="0€"
                        maxLabel="5 000€"
                        minDistance={100}
                    />
                </FormGroup>
                <FormGroup>
                    <FormLabel>{filterLabels.resumeScore}</FormLabel>
                    <InputSliderRangeStatic
                        name="resumeScore"
                        min={0}
                        max={100}
                        defaultMin={0}
                        defaultMax={100}
                        units="%"
                        minLabel="0%"
                        maxLabel="100%"
                        minDistance={0}
                    />
                </FormGroup>
                {isUpercut && (
                    <FormGroup>
                        <FormLabel>{filterLabels.upercutAssigneeIds}</FormLabel>
                        <InputAutocomplete
                            id="upercutAssigneeIds"
                            name="upercutAssigneeIds"
                            multiple
                            loading={filtersLoading}
                            options={withCount(availableFilters.upercutAssigneeIds)}
                        />
                    </FormGroup>
                )}
            </FilterDrawer>
        </FormProvider>
    );
};
